import React from 'react';

import { Box, Skeleton } from '@mui/material';
import {
    ExclamationInCircleSmall,
    TickInCircleSmall,
} from '@apigrowfinance/pbolt-icons';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';

import { RepaymentStatus } from '@enums/lending/repaymentStatus';

export interface StatusTagProps {
    status: RepaymentStatus;
    dueDate: string;
    isLoading: boolean;
}

const StatusTag = ({ status, dueDate, isLoading = false }: StatusTagProps) => {
    const { theme } = useTheme();
    if (isLoading) {
        return (
            <Skeleton
                sx={{ mr: 3 }}
                width={50}
                height={25}
                data-testid="status-tag-loader"
            />
        );
    }
    const today = dayjs();
    const daysPassed = Math.abs(today.diff(dueDate, 'day'));
    return (
        <Box
            sx={{
                mr: 3,
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
            }}>
            {status === RepaymentStatus.SCHEDULED && (
                <Tag
                    color="default"
                    label={
                        <FormattedMessage
                            id="DisbursementsPanelItem.scheduled"
                            defaultMessage="Scheduled"
                        />
                    }
                    filled
                />
            )}
            {(status === RepaymentStatus.PAID ||
                status === RepaymentStatus.PROCESSING) && (
                <Tag
                    color="success"
                    icon={
                        <TickInCircleSmall
                            sx={{ pt: 0.7 }}
                            fill={theme.palette.success.main}
                        />
                    }
                    label={
                        <FormattedMessage
                            id="RepaymentsSchedulePanel.paid"
                            defaultMessage="Paid"
                        />
                    }
                    filled
                />
            )}
            {status === RepaymentStatus.PAST_DUE && (
                <Tag
                    color="error"
                    icon={
                        <ExclamationInCircleSmall
                            sx={{ pt: 0.7 }}
                            fill={theme.palette.error.dark}
                        />
                    }
                    label={
                        <FormattedMessage
                            id="DisbursementsPanelItem.daysDue"
                            defaultMessage="{message} days overdue"
                            values={{ message: daysPassed }}
                        />
                    }
                    filled
                />
            )}
        </Box>
    );
};

export default StatusTag;
